import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { getSubjectPillClass, resolveSubjectName } from '@/lib/subjectVisuals';
import RichChatMessage from '@/components/chat/RichChatMessage';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, LogOut, School as SchoolIcon, MessageCircle,
  Mic, Camera, Type, Clock, ChevronRight, Lock, Sparkles,
} from 'lucide-react';

const MODE_ICONS = { text: MessageCircle, voice: Mic, image: Camera };

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export default function SchoolTeacherChatView() {
  const { enrollmentId, conversationId } = useParams();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [school, setSchool] = useState(null);
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [activeSubject, setActiveSubject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(null);
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [missionMeta, setMissionMeta] = useState(null);
  const [convLoading, setConvLoading] = useState(false);

  const loadList = useCallback(async (subj) => {
    setLoading(true);
    setBlocked(null);
    try {
      const meRes = await api.get('/auth/me');
      setMe(meRes.data);
      const schoolPromise = meRes.data?.school_id
        ? api.get(`/schools/${meRes.data.school_id}`)
        : Promise.resolve({ data: null });
      const params = subj && subj !== 'all' ? { subject: subj } : {};
      const [schoolRes, listRes, sumRes] = await Promise.all([
        schoolPromise,
        api.get(`/teacher/me/students/${enrollmentId}/conversations`, { params }),
        api.get(`/teacher/me/students/${enrollmentId}/summary`),
      ]);
      setSchool(schoolRes.data);
      setData(listRes.data);
      setSummary(sumRes.data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || 'Unable to load chats';
      if (status === 403) setBlocked(detail);
      else toast.error(detail);
    } finally {
      setLoading(false);
    }
  }, [enrollmentId]);

  const loadConv = useCallback(async (cid) => {
    setConvLoading(true);
    try {
      const res = await api.get(`/teacher/me/conversations/${cid}`);
      setConv(res.data.conversation);
      setMessages(res.data.messages || []);
      setMissionMeta(res.data.mission || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unable to load conversation');
    } finally {
      setConvLoading(false);
    }
  }, []);

  useEffect(() => { loadList(activeSubject); }, [loadList, activeSubject]);
  useEffect(() => {
    if (conversationId) loadConv(conversationId);
    else { setConv(null); setMessages([]); setMissionMeta(null); }
  }, [conversationId, loadConv]);

  const handleLogout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    localStorage.removeItem('yukti_session_token');
    navigate(school?.slug ? `/school/${school.slug}/login` : '/', { replace: true });
  };

  const brandColor = school?.brand_color || '#4F46E5';
  const allowedSubjects = useMemo(() => data?.allowed_subjects || [], [data]);
  const conversations = data?.conversations || [];

  const goBackToStudentList = () => {
    // Use child_id-less path — just go to teacher home; we don't know the class id here
    navigate('/school-teacher');
  };

  const openConv = (cid) => {
    navigate(`/school-teacher/students/${enrollmentId}/chats/${cid}`);
  };

  const closeConv = () => {
    navigate(`/school-teacher/students/${enrollmentId}/chats`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50" data-testid="school-teacher-chat-view">
      <header
        className="border-b bg-white/90 backdrop-blur sticky top-0 z-10"
        style={{ borderTopWidth: 4, borderTopColor: brandColor }}
      >
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {school?.logo_url ? (
              <img src={school.logo_url} alt={school.name} className="w-10 h-10 rounded-lg object-contain bg-white border" />
            ) : (
              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: brandColor }}>
                <SchoolIcon size={20} />
              </div>
            )}
            <div>
              <h1 className="font-bold text-slate-900">{school?.name || 'Your School'}</h1>
              <p className="text-xs text-slate-500">{me?.email} · Teacher</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut size={16} className="mr-1.5" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6">
        <Button
          variant="ghost" size="sm"
          onClick={goBackToStudentList}
          className="mb-3"
          data-testid="back-to-roster-btn"
        >
          <ArrowLeft size={14} className="mr-1.5" /> Back to dashboard
        </Button>

        {blocked ? (
          <div
            className="border border-amber-200 bg-amber-50 rounded-xl p-8 text-center"
            data-testid="visibility-blocked-state"
          >
            <Lock size={28} className="text-amber-500 mx-auto mb-3" />
            <p className="text-amber-900 font-semibold">{blocked}</p>
          </div>
        ) : (
          <>
            {/* Student summary */}
            {summary && (
              <div className="bg-white border rounded-xl p-5 mb-4" data-testid="student-summary-card">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                      style={{ backgroundColor: brandColor }}
                    >
                      {(summary.student.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-bold text-slate-900 text-lg" data-testid="student-summary-name">
                        {summary.student.name}
                      </div>
                      <div className="text-xs text-slate-500" data-testid="student-summary-grade">
                        Grade {summary.student.grade || '—'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm" data-testid="student-summary-stats">
                    <div>
                      <div className="text-xs text-slate-500">Total chats</div>
                      <div className="font-bold text-slate-900">
                        {Object.values(summary.chat_count_by_subject || {}).reduce((a, b) => a + b, 0)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Total XP</div>
                      <div className="font-bold text-slate-900">{summary.student.total_xp || 0}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Streak</div>
                      <div className="font-bold text-slate-900">{summary.student.streak || 0} 🔥</div>
                    </div>
                    {/* Per-student Analytics button hidden Jun 2026 — surface
                        has multiple known issues (bogus subject buckets, dead
                        single-subject chip strip, broken totals).
                        TODO: re-enable after fixing analytics.py subject
                        resolution + scoping to teacher's allowed_subjects. */}
                  </div>
                </div>
              </div>
            )}

            {/* Subject filter chips — only shown when teacher teaches 2+ subjects */}
            {allowedSubjects.length >= 2 && (
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Badge
                  onClick={() => setActiveSubject(null)}
                  variant={!activeSubject ? 'default' : 'outline'}
                  className="cursor-pointer"
                  data-testid="subject-filter-chip-all"
                >
                  All my subjects
                </Badge>
                {allowedSubjects.map((s) => (
                  <Badge
                    key={s}
                    onClick={() => setActiveSubject(s)}
                    variant={activeSubject === s ? 'default' : 'outline'}
                    className="cursor-pointer"
                    data-testid={`subject-filter-chip-${s}`}
                  >
                    {s}
                  </Badge>
                ))}
              </div>
            )}

            {/* Two-pane on desktop, stacked on mobile */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* List */}
              <div className={`lg:col-span-1 ${conv ? 'hidden lg:block' : ''}`}>
                {conversations.length === 0 ? (
                  <div className="border-2 border-dashed rounded-xl py-10 text-center bg-white/60" data-testid="conversation-empty-state">
                    <p className="text-slate-700 font-semibold">No conversations yet</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {activeSubject
                        ? `${summary?.student?.name || 'This student'} hasn't chatted about ${activeSubject} yet.`
                        : "Nothing to show in the subjects you teach."}
                    </p>
                  </div>
                ) : (
                  <div className="bg-white border rounded-xl overflow-hidden" data-testid="conversation-list">
                    {conversations.map((c) => {
                      const Icon = MODE_ICONS[c.mode] || MessageCircle;
                      const isCatchup = !!c.mission_id;
                      const isActive = conversationId === c.conversation_id;
                      return (
                        <button
                          key={c.conversation_id}
                          onClick={() => openConv(c.conversation_id)}
                          className={`w-full text-left px-4 py-3 border-b last:border-0 hover:bg-slate-50 transition-colors flex items-start gap-3 ${isActive ? 'bg-indigo-50' : ''}`}
                          data-testid={`conversation-row-${c.conversation_id}`}
                        >
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0 ${isCatchup ? 'bg-amber-500' : ''}`}
                            style={isCatchup ? undefined : { backgroundColor: brandColor }}
                          >
                            {isCatchup ? <Sparkles size={14} /> : <Icon size={14} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-900 text-sm truncate">
                              {c.title || 'New conversation'}
                            </div>
                            <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5 flex-wrap">
                              {isCatchup ? (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] py-0 px-1.5 bg-amber-50 text-amber-800 border-amber-300"
                                  data-testid={`catchup-badge-${c.conversation_id}`}
                                >
                                  Catch-up
                                </Badge>
                              ) : (
                                c.subject && <Badge variant="outline" className="text-[10px] py-0 px-1.5">{c.subject}</Badge>
                              )}
                              <Clock size={10} />
                              {timeAgo(c.updated_at)}
                              <span>· {c.message_count || 0} msgs</span>
                            </div>
                          </div>
                          <ChevronRight size={14} className="text-slate-300 mt-1 flex-shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Thread */}
              <div className={`lg:col-span-2 ${!conv ? 'hidden lg:block' : ''}`}>
                {!conversationId ? (
                  <div className="border-2 border-dashed rounded-xl py-12 text-center bg-white/60 hidden lg:block" data-testid="message-thread-empty">
                    <MessageCircle size={28} className="text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500 text-sm">Pick a conversation to view</p>
                  </div>
                ) : convLoading ? (
                  <div className="bg-white border rounded-xl p-6 flex items-center justify-center min-h-[300px]">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                  </div>
                ) : (
                  <div className="bg-white border rounded-xl overflow-hidden" data-testid="message-thread">
                    <div className="px-4 py-3 border-b flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-slate-900 flex items-center gap-2">
                          {missionMeta && (
                            <Sparkles size={14} className="text-amber-500" />
                          )}
                          {conv?.title || 'Conversation'}
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-1.5">
                          {missionMeta ? (
                            <span className="text-amber-700 font-medium">Catch-up</span>
                          ) : (
                            conv?.subject && (
                              <span className={`text-[10px] px-2 py-0.5 rounded-full ${getSubjectPillClass(conv.subject)}`}>
                                {resolveSubjectName(conv.subject) || conv.subject}
                              </span>
                            )
                          )}
                          {conv?.mode && <span>· {conv.mode}</span>}
                        </div>
                      </div>
                      <Button
                        variant="ghost" size="sm"
                        onClick={closeConv}
                        className="lg:hidden"
                        data-testid="close-conversation-btn"
                      >
                        <ArrowLeft size={14} className="mr-1" /> Back
                      </Button>
                    </div>

                    {missionMeta && (
                      <div className="px-4 py-3 bg-amber-50/60 border-b border-amber-100"
                           data-testid="mission-meta-strip">
                        <div className="text-sm text-slate-800 font-medium mb-1">
                          {missionMeta.concept_tag || 'Untitled topic'}
                        </div>
                        {missionMeta.source_assessment_title && (
                          <div className="text-[11px] text-slate-500 mb-1">
                            From: <span className="italic">{missionMeta.source_assessment_title}</span>
                          </div>
                        )}
                        <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                          {typeof missionMeta.exchanges_count === 'number' && (
                            <span>{missionMeta.exchanges_count} turns</span>
                          )}
                          {missionMeta.completion_signal && (
                            <span>· {missionMeta.completion_signal.replace(/_/g, ' ')}</span>
                          )}
                        </div>
                        {missionMeta.completion_rationale && (
                          <div className="mt-2 text-xs italic text-slate-600 border-l-2 border-amber-300 pl-2"
                               data-testid="mission-meta-rationale">
                            "{missionMeta.completion_rationale}"
                          </div>
                        )}
                      </div>
                    )}

                    <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
                      {messages.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-6">No messages yet</p>
                      ) : messages.map((m, i) => {
                        const isStudent = m.role === 'child' || m.role === 'user';
                        const studentName = summary?.student?.name || 'Student';
                        return (
                        <div
                          key={i}
                          className={`flex ${isStudent ? 'justify-end' : 'justify-start'}`}
                          data-testid={`message-row-${i}`}
                        >
                          <div
                            className={`max-w-[85%] rounded-2xl px-3 py-2 ${
                              isStudent
                                ? 'bg-orange-50 border border-orange-200 text-slate-900'
                                : 'bg-slate-100 text-slate-800'
                            }`}
                          >
                            <div className={`text-xs font-medium mb-1 ${isStudent ? 'text-orange-600' : 'text-slate-500'}`}>
                              {isStudent ? studentName : 'Yukti'}
                            </div>
                            <div className="overflow-x-auto">
                              <RichChatMessage text={m.content || m.text || ''} />
                            </div>
                          </div>
                        </div>
                      )})}
                    </div>
                    <div className="px-4 py-2 border-t bg-slate-50 text-[11px] text-slate-400 italic">
                      Read-only · You cannot reply from this view
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
